"use client";

import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import CustomInput from "./CustomInput";
import { driverAuthFormSchema } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { driverSignIn } from "@/lib/actions/driver.actions";

const DriverAuthForm = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // 1. Define your form.
  const form = useForm<z.infer<typeof driverAuthFormSchema>>({
    resolver: zodResolver(driverAuthFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // 2. Define a submit handler.
  const onSubmit = async (data: z.infer<typeof driverAuthFormSchema>) => {
    setError("");
    setIsLoading(true);
    try {
      const userData = {
        email: data.email.trim(),
        password: data.password,
      };
      const response = await driverSignIn(userData);
      if (response && response.error) {
        setError(response.error);
      }
    } catch (err) {
      console.error("DriverAuthForm/onSubmit", err);
      setError("Une erreur réseau s'est produite. Veuillez réessayer !");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="auth-form">
      <header className="flex flex-col gap-5 md:gap-8">
        <Link
          href="https://www.taxisconnect.com/"
          className="flex cursor-pointer items-center gap-1"
        >
          <Image
            src="/icons/icon@2x.png"
            alt="TaxisConnect Logo"
            width={34}
            height={40}
            className="rounded-md"
          />
          <h1 className="text-26 font-ibm-plex-serif font-bold text-black-1 ml-2">
            TaxisConnect Chauffeur
          </h1>
        </Link>
      </header>
      <div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <CustomInput
              control={form.control}
              name={"email"}
              label={"Votre adresse email"}
              placeholder={"exemple@email.com"}
            />
            <CustomInput
              control={form.control}
              name={"password"}
              label={"Mot de passe"}
              placeholder={"Indiquez votre mot de passe"}
              inputType="password"
            />
            {error && (
              <div className="text-red-500 text-sm font-medium">{error}</div>
            )}

            <div className="flex flex-col gap-4">
              <Button type="submit" className="form-btn" disabled={isLoading}>
                {isLoading ? (
                  <div className="flex">
                    <Loader2 size={20} className="animate-spin" /> &nbsp;
                    Chargement...
                  </div>
                ) : (
                  "Se connecter"
                )}
              </Button>
            </div>
          </form>
        </Form>
        <footer className="flex justify-center gap-2 flex-col">
          <div className="text-12 font-normal text-gray-600 mt-2">
            Pour nous joindre :{" "}
            <a
              href="mailto:commercial@taxisconnect.com"
              className="underline"
            >
              commercial@taxisconnect.com
            </a>{" "}
            ou 09 75 120 120 (appel local) aux heures de bureau
          </div>
          <div className="flex flex-col justify-between gap-2">
            <Link href={"/driver-forgotten-password"}>
              <p className="text-14 font-semibold text-blue-600">
                Mot de passe oublié ?
              </p>
            </Link>
            <Link href={"/sign-in"}>
              <p className="text-14 font-semibold text-blue-600">
                Se connecter comme client
              </p>
            </Link>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default DriverAuthForm;

